"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { Row, Col, Card, Checkbox, Button, Spin, Collapse, Select, Pagination, message, Carousel } from "antd"
import {
  ShoppingCartOutlined,
  ControlOutlined,
  CaretRightOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons"
import "../assets/styles/Landing.css"
import "../assets/styles/Carousel.css"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import { searchProducts } from "../services/ProductServices"
import { useAuth } from "../contexts/authContext/index"
import { getLandingPageImages } from "../services/LandingPageServices"

const REACT_APP_BFF_URL = process.env.REACT_APP_BFF_URL

const Landing = () => {
  const [showFilters, setShowFilters] = useState(window.innerWidth >= 992)
  const [loading, setLoading] = useState(true)
  const [products, setProducts] = useState([])
  const [totalProducts, setTotalProducts] = useState(0)
  const [categories, setCategories] = useState([])
  const [selectedCategories, setSelectedCategories] = useState([])
  const [certifications, setCertifications] = useState([])
  const [selectedCertifications, setSelectedCertifications] = useState([])
  const [materials, setMaterials] = useState([])
  const [selectedMaterials, setSelectedMaterials] = useState([])
  const [filtersCollapsed, setFiltersCollapsed] = useState(false)
  const [sortOrder, setSortOrder] = useState("default")
  const [currentPage, setCurrentPage] = useState(1)
  const [filterOverlay, setFilterOverlay] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const { userLoggedIn, getToken } = useAuth()
  const { searchTerm: urlSearchTerm } = useParams()
  const location = useLocation()
  const [imagesData, setImagesData] = useState([])
  const [imagesLoading, setImagesLoading] = useState(true)
  const carouselRef = useRef()

  const normalizeProduct = useCallback((product) => {
    const fallbackImage =
      product.imageUrl ||
      (Array.isArray(product.images) && product.images.length > 0 ? product.images[0] : "")

    return {
      id: product.id,
      name: product.name,
      price: product.price || 300,
      inStock: product.inStock !== undefined ? product.inStock : true,
      imageUrl: fallbackImage,
      category: product.category || "Sin categoría",
      certifications: product.certifications || [],
      materials: product.materials || [],
    }
  }, [])

  // Memoize fetchImages function to prevent unnecessary re-renders
  const fetchImages = useCallback(async () => {
    try {
      setImagesLoading(true)
      const token = await getToken()
      const images = await getLandingPageImages(token)
      setImagesData(images)
    } catch (error) {
      console.error("Error fetching landing page images:", error)
      message.error("Error al cargar las imágenes")
    } finally {
      setImagesLoading(false)
    }
  }, [getToken]) // Add getToken as a dependency

  // Fetch all images on component mount
  useEffect(() => {
    fetchImages()
  }, [fetchImages])

  // Extract search term from URL
  useEffect(() => {
    if (urlSearchTerm) {
      setSearchTerm(decodeURIComponent(urlSearchTerm))
      setCurrentPage(1)
    } else {
      setSearchTerm("")
    }
  }, [urlSearchTerm, location])

  const getPageSize = useCallback(() => {
    if (typeof window !== "undefined") {
      if (window.innerWidth >= 1200) return 24 // xl
      if (window.innerWidth >= 992) return 20 // lg
      if (window.innerWidth >= 768) return 16 // md
      if (window.innerWidth >= 576) return 12 // sm
      return 8 // xs
    }
    return 24 // default for server-side rendering
  }, [])

  const [pageSize, setPageSize] = useState(getPageSize())

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true)
      const response = await searchProducts({
        query: searchTerm.trim(),
        categories: selectedCategories,
        certifications: selectedCertifications,
        materials: selectedMaterials,
        sortOrder,
        page: currentPage,
        pageSize,
      })

      const normalizedItems = Array.isArray(response?.items)
        ? response.items.map((product) => normalizeProduct(product))
        : []

      setProducts(normalizedItems)
      setTotalProducts(response?.total || 0)

      if (response?.availableFilters) {
        if (Array.isArray(response.availableFilters.categories)) {
          setCategories(response.availableFilters.categories)
        }
        if (Array.isArray(response.availableFilters.certifications)) {
          setCertifications(response.availableFilters.certifications)
        }
        if (Array.isArray(response.availableFilters.materials)) {
          setMaterials(response.availableFilters.materials)
        }
      }
    } catch (error) {
      console.error("Error loading products:", error)
      message.error("Error al cargar los productos")
    } finally {
      setLoading(false)
    }
  }, [searchTerm, selectedCategories, selectedCertifications, selectedMaterials, sortOrder, currentPage, pageSize, normalizeProduct])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, selectedCategories, selectedCertifications, selectedMaterials, sortOrder, pageSize])

  useEffect(() => {
    fetchProducts()
  }, [fetchProducts])

  useEffect(() => {
    const handleResize = () => {
      setPageSize(getPageSize())
      setShowFilters(window.innerWidth >= 992)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [getPageSize])

  const getImageType = (base64) => {
    if (base64.startsWith("data:image/png")) {
      return "image/png"
    } else if (base64.startsWith("data:image/jpeg")) {
      return "image/jpeg"
    } else {
      return "image" // Default if no match
    }
  }

  useEffect(() => {
    const handleResize = () => {
      setShowFilters(window.innerWidth >= 992)
      // Force re-render to update button text
      setFilterOverlay((prev) => !prev)
      setFilterOverlay((prev) => !prev)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const toggleFilters = () => {
    if (window.innerWidth < 992) {
      setFilterOverlay(!filterOverlay)
    } else {
      setShowFilters(!showFilters)
      setFiltersCollapsed(!filtersCollapsed)
    }
  }


  const handleCategoryChange = (category) => {
    setSelectedCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category],
    )
  }

  const handleCertificationChange = (certification) => {
    setSelectedCertifications((prev) =>
      prev.includes(certification) ? prev.filter((c) => c !== certification) : [...prev, certification],
    )
  }

  const handleMaterialChange = (material) => {
    setSelectedMaterials((prev) => (prev.includes(material) ? prev.filter((m) => m !== material) : [...prev, material]))
  }

  // const handlePriceChange = (value) => {
  //   setPriceRange(value)
  //   setMinPrice(value[0])
  //   setMaxPrice(value[1])
  // }

  // const handleMinPriceChange = (e) => {
  //   const value = Number(e.target.value)
  //   setMinPrice(value)
  //   setPriceRange([value, priceRange[1]])
  // }

  // const handleMaxPriceChange = (e) => {
  //   const value = Number(e.target.value)
  //   setMaxPrice(value)
  //   setPriceRange([priceRange[0], value])
  // }

  const handleSortChange = (value) => {
    setSortOrder(value)
  }

  const resetFiltersAndSort = () => {
    setSelectedCategories([])
    setSelectedCertifications([])
    setSelectedMaterials([])
    // setMinPrice(0)
    // setMaxPrice(5000)
    setSortOrder("default")
    setCurrentPage(1)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const navigate = useNavigate()

  const addToCart = useCallback(
    (product) => {
      if (!userLoggedIn) {
        navigate("/signin")
        return
      }

      navigate(`/producto/${product.id}`)
    },
    [navigate, userLoggedIn],
  )

  const handleProductClick = useCallback(
    (productId) => {
      navigate(`/producto/${productId}`)
    },
    [navigate],
  )

  const isLoading = loading

  return (
    <div className="product-landing">
      <Row className="hero-section">
        <Col span={24}>
          {imagesLoading ? (
            <div style={{ height: "400px", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Spin size="large" />
            </div>
          ) : imagesData.length > 0 ? (
            <div style={{ position: "relative" }}>
              <Carousel autoplay effect="fade" className="hero-carousel" ref={carouselRef}>
                {imagesData
                  .sort((a, b) => a.position - b.position) // Ordenar por posición de menor a mayor
                  .map((image) => (
                    <div key={image.id} className="carousel-item">
                      <img
                        src={image.image_path ? `${REACT_APP_BFF_URL}/api/file-upload/download?path=${encodeURIComponent(image.image_path)}` : "/placeholder.svg"}
                        alt={`Imagen hero ${image.position}`}
                        className="hero-image"
                      />
                    </div>
                  ))}
              </Carousel>
              <div className="carousel-arrow carousel-arrow-prev" onClick={() => carouselRef.current.prev()}>
                <LeftOutlined />
              </div>
              <div className="carousel-arrow carousel-arrow-next" onClick={() => carouselRef.current.next()}>
                <RightOutlined />
              </div>
            </div>
          ) : (
            <img
              src="https://images.unsplash.com/photo-1581094288338-2314dddb7ece?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80"
              alt="Cañerías y flujo de agua"
              className="hero-image"
            />
          )}
        </Col>
      </Row>
      <Row justify="space-between" align="middle" style={{ marginBottom: "20px" }}>
        <Col xs={2} md={6}>
          <Button type="primary" icon={<ControlOutlined />} onClick={toggleFilters} className="filter-button">
            {window.innerWidth < 768
              ? ""
              : showFilters
                ? filtersCollapsed
                  ? "Expandir Filtros"
                  : "Colapsar Filtros"
                : "Mostrar Filtros"}
          </Button>
        </Col>
        <Col xs={15} md={6} className="centersort">
          <span className="subcolor">{window.innerWidth < 576 ? "Ordenar: " : "Ordenar por: "}</span>
          <Select
            value={sortOrder}
            style={{ width: "60%" }}
            onChange={handleSortChange}
            placeholder="Ordenar por"
            className="centersortcont"
          >
            <Select.Option value="default">Últimos</Select.Option>
            <Select.Option value="name-asc">Nombre A-Z</Select.Option>
            <Select.Option value="name-desc">Nombre Z-A</Select.Option>
            {/* <Select.Option value="price-asc">Menor precio</Select.Option>
            <Select.Option value="price-desc">Mayor precio</Select.Option> */}
          </Select>
        </Col>
        <Col xs={7} md={6} style={{ textAlign: "right" }}>
          <span>{totalProducts}</span>
          <span className="subcolor"> {window.innerWidth < 768 ? "elementos" : "elementos encontrados"}</span>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col xs={24} md={6}>
          {(showFilters || filterOverlay) && (
            <div className={`filters-overlay ${filterOverlay ? "active" : ""}`}>
              <Card className={`filters-card ${filtersCollapsed ? "collapsed" : ""}`}>
                <Collapse
                  defaultActiveKey={["1", "2", "3"]}
                  expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                >
                  <Collapse.Panel header="Categorías" key="1">
                    <div className="category-filters">
                      {categories.map((category) => (
                        <Checkbox
                          className="checkbox-width"
                          key={category}
                          onChange={() => handleCategoryChange(category)}
                          checked={selectedCategories.includes(category)}
                        >
                          {category}
                        </Checkbox>
                      ))}
                    </div>
                  </Collapse.Panel>

                  <Collapse.Panel header="Certificaciones" key="2">
                    <div className="certification-filters">
                      {certifications.map((certification) => (
                        <Checkbox
                          className="checkbox-width"
                          key={certification}
                          onChange={() => handleCertificationChange(certification)}
                          checked={selectedCertifications.includes(certification)}
                        >
                          {certification}
                        </Checkbox>
                      ))}
                    </div>
                  </Collapse.Panel>

                  <Collapse.Panel header="Materiales" key="3">
                    <div className="material-filters">
                      {materials.map((material) => (
                        <Checkbox
                          className="checkbox-width"
                          key={material}
                          onChange={() => handleMaterialChange(material)}
                          checked={selectedMaterials.includes(material)}
                        >
                          {material}
                        </Checkbox>
                      ))}
                    </div>
                  </Collapse.Panel>

                  {/* <Collapse.Panel header="Precio" key="4">
                    <div className="price-input-container">
                      <Input
                        type="number"
                        value={minPrice}
                        onChange={handleMinPriceChange}
                        prefix="$"
                        className="priceInput"
                      />
                      -
                      <Input
                        type="number"
                        value={maxPrice}
                        onChange={handleMaxPriceChange}
                        prefix="$"
                        className="priceInput"
                      />
                    </div>
                    <Slider
                      range
                      value={priceRange}
                      onChange={handlePriceChange}
                      min={0}
                      max={5000}
                      marks={{
                        0: "$0",
                        5000: "$5000",
                      }}
                    />
                  </Collapse.Panel> */}

                </Collapse>
                <Button onClick={resetFiltersAndSort} style={{ marginTop: "10px", width: "100%" }}>
                  Restablecer Filtros
                </Button>
              </Card>
            </div>
          )}
        </Col>
        {filterOverlay && <div className="filter-backdrop" onClick={toggleFilters}></div>}
        <Col xs={24} md={showFilters ? (filtersCollapsed ? 18 : 18) : 24}>
          {isLoading ? (
            <div className="loading-container">
              <Spin size="large" />
            </div>
          ) : (
            <>
              {searchTerm && (
                <div className="search-results-header" style={{ marginBottom: "20px" }}>
                  <h2>Resultados para: "{searchTerm}"</h2>
                  {/* <p>{totalProducts} productos encontrados</p> */}
                  <Button
                    type="link"
                    onClick={() => {
                      navigate("/")
                      setSearchTerm("")
                    }}
                    style={{ padding: 0 }}
                  >
                    Limpiar búsqueda
                  </Button>
                </div>
              )}
              <Row gutter={[16, 16]}>
                {products.map((product) => (
                  <Col
                    xs={12}
                    sm={12}
                    md={8}
                    lg={showFilters ? (filtersCollapsed ? 8 : 8) : 6}
                    xl={showFilters ? (filtersCollapsed ? 6 : 6) : 6}
                    key={product.id}
                  >
                    <Card
                      hoverable
                      className="product-card"
                      onClick={() => handleProductClick(product.id)}
                      cover={
                        <div className="image-container">
                          {product.imageUrl ? (
                            <img
                              src={product.imageUrl || "/placeholder.svg"}
                              alt={product.name || "Producto"}
                              className="productt-image"
                              type={getImageType(product.imageUrl)}
                            />
                          ) : (
                            <div className="no-image">Sin imagen</div>
                          )}
                        </div>
                      }
                    >
                      <div className="product-details">
                        <h3 className="product-title">{product.name || "Producto sin nombre"}</h3>
                        {/* <div className="product-price-container">
                          <span className="product-price">${(product.price || 0).toFixed(2)}</span>
                        </div> */}
                      </div>
                      <Button
                        type="primary"
                        icon={<ShoppingCartOutlined />}
                        className="add-to-cart-button"
                        onClick={(e) => {
                          e.stopPropagation()
                          addToCart(product)
                        }}
                        aria-label={`Añadir ${product.name || "Producto"} al carrito`}
                      />
                    </Card>
                  </Col>
                ))}
              </Row>
              <Row justify="center" style={{ marginTop: "20px" }}>
                <Pagination
                  current={currentPage}
                  total={totalProducts}
                  pageSize={pageSize}
                  onChange={handlePageChange}
                  locale={{ jump_to: "Ir a", page: "página" }}
                  showSizeChanger={false}
                  showQuickJumper={true}
                />
              </Row>
            </>
          )}
        </Col>
      </Row>
    </div>
  )
}

export default Landing

